import { useCallback } from 'react';

const SNAPSHOT_NODE_WIDTH = 260;
const SNAPSHOT_NODE_HEIGHT = 110;

const escapeXml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const buildGraphSnapshotSvg = (flowNodes, flowEdges) => {
  const nodes = Array.isArray(flowNodes) ? flowNodes : [];
  const edges = Array.isArray(flowEdges) ? flowEdges : [];
  if (nodes.length === 0) {
    throw new Error('No nodes available for export.');
  }

  const byId = {};
  nodes.forEach((node) => {
    byId[node.id] = node;
  });

  const xValues = nodes.map((node) => Number(node?.position?.x) || 0);
  const yValues = nodes.map((node) => Number(node?.position?.y) || 0);
  const minX = Math.min(...xValues);
  const minY = Math.min(...yValues);
  const maxX = Math.max(...xValues);
  const maxY = Math.max(...yValues);
  const margin = 48;

  const width = Math.max(720, Math.round(maxX - minX + SNAPSHOT_NODE_WIDTH + margin * 2));
  const height = Math.max(420, Math.round(maxY - minY + SNAPSHOT_NODE_HEIGHT + margin * 2));

  const mapX = (x) => Math.round((Number(x) || 0) - minX + margin);
  const mapY = (y) => Math.round((Number(y) || 0) - minY + margin);

  const statusColor = (status) => {
    if (status === 'executed' || status === 'completed') return '#48BB78';
    if (status === 'executing') return '#F6AD55';
    if (status === 'edited') return '#4299E1';
    if (status === 'error') return '#F56565';
    return '#CBD5E0';
  };

  const edgeSvg = edges
    .map((edge) => {
      const source = byId[edge.source];
      const target = byId[edge.target];
      if (!source || !target) return '';

      const sourceX = mapX(source.position?.x) + SNAPSHOT_NODE_WIDTH / 2;
      const sourceY = mapY(source.position?.y) + SNAPSHOT_NODE_HEIGHT / 2;
      const targetX = mapX(target.position?.x) + SNAPSHOT_NODE_WIDTH / 2;
      const targetY = mapY(target.position?.y) + SNAPSHOT_NODE_HEIGHT / 2;

      return `<line x1="${sourceX}" y1="${sourceY}" x2="${targetX}" y2="${targetY}" stroke="#A0AEC0" stroke-width="2" marker-end="url(#arrow)" />`;
    })
    .join('\n');

  const nodeSvg = nodes
    .map((node) => {
      const x = mapX(node.position?.x);
      const y = mapY(node.position?.y);
      const label = escapeXml(node.data?.label || `Node ${node.id}`);
      const status = escapeXml(node.data?.status || 'unedited');
      const statusText = status.charAt(0).toUpperCase() + status.slice(1);
      const include = Number(node.data?.criteriaCount?.include) || 0;
      const exclude = Number(node.data?.criteriaCount?.exclude) || 0;
      const patients =
        Number.isFinite(node.data?.patientCount) &&
        (node.data?.status === 'executed' ||
          node.data?.status === 'completed' ||
          node.data?.isExecuted === true)
          ? Number(node.data.patientCount).toLocaleString()
          : null;
      const border = statusColor(node.data?.status);

      return `
        <g transform="translate(${x}, ${y})">
          <rect x="0" y="0" rx="12" ry="12" width="${SNAPSHOT_NODE_WIDTH}" height="${SNAPSHOT_NODE_HEIGHT}" fill="#FFFFFF" stroke="${border}" stroke-width="2" />
          <text x="14" y="26" font-size="16" font-family="Inter, Arial, sans-serif" font-weight="700" fill="#2D3748">${label}</text>
          <text x="14" y="48" font-size="12" font-family="Inter, Arial, sans-serif" fill="#718096">${include} Include, ${exclude} Exclude</text>
          <text x="14" y="68" font-size="12" font-family="Inter, Arial, sans-serif" fill="${border}" font-weight="700">${escapeXml(statusText)}</text>
          ${
            patients
              ? `<text x="${SNAPSHOT_NODE_WIDTH - 14}" y="68" text-anchor="end" font-size="12" font-family="Inter, Arial, sans-serif" fill="#2D3748" font-weight="700">${escapeXml(patients)} patients</text>`
              : ''
          }
        </g>
      `;
    })
    .join('\n');

  return {
    width,
    height,
    svg: `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <defs>
    <marker id="arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
      <path d="M0,0 L0,6 L9,3 z" fill="#A0AEC0" />
    </marker>
  </defs>
  <rect x="0" y="0" width="${width}" height="${height}" fill="#F8F9FA" />
  ${edgeSvg}
  ${nodeSvg}
</svg>`,
  };
};

const downloadGraphJson = (payload) => {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `cohort-graph-${stamp}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const downloadSnapshotSvg = (flowNodes, flowEdges) => {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const { svg } = buildGraphSnapshotSvg(flowNodes, flowEdges);
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `cohort-graph-${stamp}.svg`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const downloadSnapshotPng = async (flowNodes, flowEdges) => {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const { svg, width, height } = buildGraphSnapshotSvg(flowNodes, flowEdges);
  const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Could not render graph snapshot image.'));
      image.src = svgUrl;
    });

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas context is not available.');
    }
    context.drawImage(img, 0, 0);

    const blob = await new Promise((resolve) => {
      canvas.toBlob((nextBlob) => resolve(nextBlob), 'image/png');
    });
    if (!blob) {
      throw new Error('Could not export PNG.');
    }

    const pngUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = pngUrl;
    anchor.download = `cohort-graph-${stamp}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(pngUrl);
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
};

export const useGraphImportExport = ({ getGraphState, getNodes, getEdges, serializeGraph, showFeedback }) => {
  const exportJson = useCallback(() => {
    const payload = serializeGraph(getGraphState());
    downloadGraphJson(payload);
    showFeedback('Graph exported as JSON.', 'success');
  }, [getGraphState, serializeGraph, showFeedback]);

  const exportSvg = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    if (nodes.length === 0) {
      showFeedback('Create or load a graph before exporting.', 'warning');
      return;
    }
    try {
      downloadSnapshotSvg(nodes, edges);
      showFeedback('Graph exported as SVG.', 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'SVG export failed.';
      showFeedback(`SVG export failed: ${message}`, 'error');
    }
  }, [getEdges, getNodes, showFeedback]);

  const exportPng = useCallback(async () => {
    const nodes = getNodes();
    const edges = getEdges();
    if (nodes.length === 0) {
      showFeedback('Create or load a graph before exporting.', 'warning');
      return;
    }
    try {
      await downloadSnapshotPng(nodes, edges);
      showFeedback('Graph exported as PNG.', 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'PNG export failed.';
      showFeedback(`PNG export failed: ${message}`, 'error');
    }
  }, [getEdges, getNodes, showFeedback]);

  return {
    exportJson,
    exportSvg,
    exportPng,
  };
};

